/**
 * MyWorkLane — Six-section apex landing for Builder, Reviewer, and Viewer roles.
 *
 * Sections:
 *   1. Hero salutation (FR-004)
 *   2. Three-lane swimlane: DOING NOW / WAITING FOR YOUR INPUT / REVIEW REQUESTED (FR-005)
 *   3. My in-flight runs DataTable (FR-006)
 *   4. My queue DataTable with cursor pagination (FR-007)
 *   5. Decisions for you snippet (FR-008)
 *   6. My recent contributions DataTable (FR-009)
 *
 * Does NOT import useEffectiveUser — salutation uses data.userFirstName from useMyWork.
 * All routing uses navigate() from useHashLocation(); zero window.location.hash mutations.
 */
import '../../styles/screens/today.css';
import React, { useCallback, type CSSProperties } from "react";
import { useMyWork } from "../../api/hooks/useMyWork";
import { useHumanTaskQueue } from "../../api/hooks/useHumanTaskQueue";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { useHashLocation } from "../../lib/router";
import { PageShell } from "../../components/layout/PageShell";
import { ErrorState } from "../../components/primitives";
import MyWorkLaneSkeleton from "./MyWorkLaneSkeleton";
import type { DecisionItem as HumanTaskDecisionItem } from "../../api/transforms/humanTask";
import type {
  MyWorkView,
  TaskCardShape,
  DecisionItem,
  RefinementRunCard,
  TaskRowShape,
  ContributionRowShape,
} from "../../../../../shared/types/myWorkLane";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SWIMLANE_CAP = 10;
const QUEUE_DISPLAY_CAP = 25;
const DECISIONS_SNIPPET_CAP = 3;
const CONTRIBUTIONS_CAP = 10;
const IN_FLIGHT_CAP = 10;

// ---------------------------------------------------------------------------
// Inline HumanTaskSnippetRow (PRD §5.11 — verbatim)
// ---------------------------------------------------------------------------

function HumanTaskSnippetRow({
  item, onClick,
}: { item: HumanTaskDecisionItem; onClick: () => void }) {
  return (
    <div className="snippet-row" role="button" tabIndex={0}
      onClick={onClick}
      onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick())}>
      <span className="snippet-title">{item.title}</span>
      <span className="snippet-type">{item.kind}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline TaskCard (V5 closure — no deployed <TaskCard> component)
// ---------------------------------------------------------------------------

const taskCardStyle: CSSProperties = {
  padding: "12px",
  borderRadius: "8px",
  backgroundColor: "var(--color-surface, #fff)",
  border: "1px solid var(--color-border, #e0e0e0)",
  cursor: "pointer",
};

function TaskCard({
  card,
  onClick,
}: {
  card: TaskCardShape;
  onClick: () => void;
}) {
  return (
    <div
      data-testid="task-card"
      style={taskCardStyle}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) =>
        (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick())
      }
    >
      <div style={{ fontWeight: 600 }}>{card.title}</div>
      {card.contextLine && (
        <div style={{ fontSize: "0.85em", color: "var(--color-slate, #64748b)" }}>
          {card.contextLine}
        </div>
      )}
      <div style={{ fontSize: "0.8em", marginTop: "4px" }}>
        <span
          style={{
            color: card.statusColor,
            marginRight: "8px",
          }}
        >
          {card.statusLabel}
        </span>
        <span>{card.priority}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DecisionRow — for WAITING and REVIEW swimlane columns
// ---------------------------------------------------------------------------

function DecisionRow({
  item,
  onClick,
}: {
  item: DecisionItem;
  onClick: () => void;
}) {
  return (
    <div
      data-testid="task-card"
      style={{
        padding: "8px 12px",
        borderRadius: "6px",
        cursor: "pointer",
        backgroundColor: "var(--color-surface, #fff)",
        border: "1px solid var(--color-border, #e0e0e0)",
      }}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) =>
        (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick())
      }
    >
      <div style={{ fontWeight: 500 }}>{item.title}</div>
      <div style={{ fontSize: "0.85em", color: "var(--color-slate, #64748b)" }}>
        {item.taskType}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SwimlaneColumn
// ---------------------------------------------------------------------------

const columnStyle: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  minWidth: 0,
};

function SwimlaneColumn({
  title,
  count,
  variant,
  children,
  emptyText,
}: {
  title: string;
  count: number;
  variant?: "coral";
  children: React.ReactNode;
  emptyText: string;
}) {
  const ringStyle: CSSProperties | undefined =
    variant === "coral"
      ? { outline: "1px solid var(--color-coral, #f87171)", borderRadius: "8px", padding: "8px" }
      : undefined;

  return (
    <div style={columnStyle} className={variant === "coral" ? "swimlane-coral" : undefined}>
      <div style={{ fontWeight: 600, marginBottom: "4px" }}>
        {title}{" "}
        <span aria-live="polite" style={{ fontSize: "0.85em", fontWeight: 400, color: "var(--color-slate, #64748b)" }}>
          ({count})
        </span>
        {count === 0 && (
          <>
            {"\n"}
            {emptyText}
          </>
        )}
      </div>
      {count > 0 && <div style={ringStyle}>{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DataTable — inline lightweight table (no deployed <DataTable> component)
// ---------------------------------------------------------------------------

interface DataTableColumn<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
}

function DataTable<T extends { id?: string; runId?: string }>({
  rows,
  columns,
  onRowClick,
  appendOnly,
  rowTestId,
  emptyState,
}: {
  rows: T[];
  columns: DataTableColumn<T>[];
  onRowClick?: (row: T) => void;
  appendOnly?: boolean;
  rowTestId?: string;
  emptyState?: React.ReactNode;
}) {
  if (rows.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <table role="table" style={{ width: "100%", borderCollapse: "collapse" }} data-append-only={appendOnly}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              style={{
                textAlign: "left",
                padding: "8px",
                borderBottom: "1px solid var(--color-border, #e0e0e0)",
                fontSize: "0.85em",
                fontWeight: 600,
              }}
            >
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr
            key={(row as Record<string, unknown>).id as string ?? (row as Record<string, unknown>).runId as string ?? idx}
            data-testid={rowTestId}
            style={{ cursor: onRowClick ? "pointer" : undefined }}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {columns.map((col) => (
              <td
                key={col.key}
                style={{
                  padding: "8px",
                  borderBottom: "1px solid var(--color-border, #e0e0e0)",
                  fontSize: "0.9em",
                }}
              >
                {col.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// PartialDataBanner (F-24 closure)
// ---------------------------------------------------------------------------

function PartialDataBanner() {
  return (
    <div
      role="status"
      data-testid="partial-data-banner"
      style={{
        padding: "12px 16px",
        backgroundColor: "var(--banner-bg-dark, #fef3c7)",
        borderRadius: "8px",
        marginBottom: "16px",
        color: "var(--color-warning-text, #92400e)",
      }}
    >
      <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>
        Partial data warning.
      </span>
      Some sections didn&apos;t load. Refresh to try again.
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section styles
// ---------------------------------------------------------------------------

const sectionStyle: CSSProperties = { marginBottom: "24px" };

const sectionHeaderStyle: CSSProperties = {
  fontWeight: 600,
  fontSize: "1.1em",
  marginBottom: "12px",
};

// ---------------------------------------------------------------------------
// MyWorkLane (main component)
// ---------------------------------------------------------------------------

export default function MyWorkLane(): React.ReactElement {
  const myWork = useMyWork();
  const decisionsHook = useHumanTaskQueue();
  const reducedMotion = useReducedMotion();
  const [, navigate] = useHashLocation();

  // ── Decisions data from the parent hook (no second subscription) ────
  const decisionsData =
    decisionsHook.status === "ready" ? decisionsHook.data : [];
  const decisionsSlice = decisionsData.slice(0, DECISIONS_SNIPPET_CAP);

  // ── Decisions tray open handler ────────────────────────────────────
  const openDecisionsTray = useCallback(() => {
    document.dispatchEvent(new CustomEvent("capicua:open-decisions"));
  }, []);

  // ── Loading state ──────────────────────────────────────────────────
  if (myWork.status === "loading") {
    return <MyWorkLaneSkeleton />;
  }

  // ── Error state ────────────────────────────────────────────────────
  if (myWork.status === "error") {
    return (
      <PageShell>
        <div data-testid="error-state">
          <ErrorState message={myWork.error} onRetry={() => window.location.reload()} />
        </div>
      </PageShell>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────
  if (myWork.status === "empty") {
    return (
      <PageShell>
        <div data-testid="empty-state" style={{ textAlign: "center", padding: "64px 16px" }}>
          <h2 style={{ marginBottom: "8px" }}>Good morning.</h2>
          <p style={{ color: "var(--color-slate, #64748b)" }}>
            Nothing here yet. Your assigned tasks, runs, and contributions will appear as you get started.
          </p>
        </div>
      </PageShell>
    );
  }

  // ── Forbidden state ────────────────────────────────────────────────
  if (myWork.status === "forbidden") {
    return (
      <PageShell>
        <ErrorState message="You do not have access to this page." />
      </PageShell>
    );
  }

  // ── Ready state ────────────────────────────────────────────────────
  const { data } = myWork;

  const salutation = data.userFirstName
    ? `Good morning, ${data.userFirstName}.`
    : "Good morning.";

  const doingNowSlice = data.doingNow.slice(0, SWIMLANE_CAP);
  const waitingSlice = data.waitingForInput.slice(0, SWIMLANE_CAP);
  const reviewSlice = data.reviewRequested.slice(0, SWIMLANE_CAP);
  const runsSlice = data.inFlightRuns.slice(0, IN_FLIGHT_CAP);
  const queueSlice = data.queue.slice(0, QUEUE_DISPLAY_CAP);
  const contributionsSlice = data.recentContributions.slice(0, CONTRIBUTIONS_CAP);

  const swimlaneClass = reducedMotion ? "swimlane" : "swimlane fade-in";

  return (
    <PageShell>
      {/* ── Partial-data banner ──────────────────────────────── */}
      {data.partial && <PartialDataBanner />}

      {/* ── 1. Hero salutation (FR-004) ──────────────────────── */}
      <div style={sectionStyle}>
        <h1 className="productDisplay">{salutation}</h1>
        <p className="bodyLarge" style={{ color: "var(--color-slate, #64748b)" }}>
          Here&apos;s where things stand.
        </p>
      </div>

      {/* ── 2. Three-lane swimlane (FR-005) ──────────────────── */}
      <div
        data-testid="swimlane"
        className={swimlaneClass}
        style={{ ...sectionStyle, display: "flex", gap: "16px" }}
      >
        <SwimlaneColumn
          title="Doing now"
          count={doingNowSlice.length}
          emptyText="Nothing in progress"
        >
          {doingNowSlice.map((card) => (
            <TaskCard
              key={card.id}
              card={card}
              onClick={() => navigate(`/task/${card.id}`)}
            />
          ))}
        </SwimlaneColumn>

        <SwimlaneColumn
          title="Waiting for your input"
          count={waitingSlice.length}
          variant="coral"
          emptyText="No decisions waiting"
        >
          {waitingSlice.map((item) => (
            <DecisionRow
              key={item.id}
              item={item}
              onClick={() => navigate(`/task/${item.id}`)}
            />
          ))}
        </SwimlaneColumn>

        <SwimlaneColumn
          title="Review requested"
          count={reviewSlice.length}
          emptyText="No reviews requested"
        >
          {reviewSlice.map((item) => (
            <DecisionRow
              key={item.id}
              item={item}
              onClick={() => navigate(`/task/${item.id}`)}
            />
          ))}
        </SwimlaneColumn>
      </div>

      {/* ── 3. My in-flight runs (FR-006) ────────────────────── */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>My in-flight runs</div>
        <DataTable<RefinementRunCard>
          rows={runsSlice}
          appendOnly={true}
          rowTestId="task-row"
          onRowClick={(run) => navigate(`/refine/${run.runId}`)}
          columns={[
            { key: "workflowName", header: "Workflow", render: (r) => r.workflowName },
            { key: "statusLabel", header: "Status", render: (r) => r.statusLabel },
            { key: "createdAtLabel", header: "Started", render: (r) => r.createdAtLabel },
            { key: "costDisplay", header: "Cost", render: (r) => r.costDisplay },
          ]}
          emptyState={
            <div style={{ opacity: 0.6, padding: "16px 0" }}>
              Nothing running. Start a run from any initiative.{" "}
              <a href="#/initiate">Start a run</a>
            </div>
          }
        />
      </div>

      {/* ── 4. My queue (FR-007) ─────────────────────────────── */}
      <div data-testid="queue-section" style={sectionStyle}>
        <div style={sectionHeaderStyle}>My queue</div>
        <DataTable<TaskRowShape>
          rows={queueSlice}
          appendOnly={true}
          rowTestId="task-row"
          onRowClick={(task) => navigate(`/task/${task.id}`)}
          columns={[
            { key: "title", header: "Task", render: (r) => r.title },
            { key: "dueDateLabel", header: "Due", render: (r) => r.dueDateLabel ?? "\u2014" },
            { key: "priority", header: "Priority", render: (r) => r.priority },
            { key: "workspace", header: "Workspace", render: (r) => r.workspace },
          ]}
          emptyState={
            <div style={{ opacity: 0.6, padding: "16px 0" }}>
              Nothing in your queue. Tasks assigned to you will appear here.
            </div>
          }
        />
        {data.hasMoreQueue && (
          <button
            data-testid="load-more-queue"
            type="button"
            onClick={myWork.loadMoreQueue}
            style={{ marginTop: "8px" }}
          >
            Load 25 more
          </button>
        )}
      </div>

      {/* ── 5. Decisions for you (FR-008) ────────────────────── */}
      {decisionsData.length > 0 && (
        <div style={sectionStyle}>
          <div style={sectionHeaderStyle}>Decisions for you</div>
          {decisionsSlice.map((task) => (
            <HumanTaskSnippetRow
              key={task.id}
              item={task}
              onClick={openDecisionsTray}
            />
          ))}
          <button type="button" onClick={openDecisionsTray} style={{ marginTop: "8px" }}>
            View all decisions
          </button>
        </div>
      )}

      {/* ── 6. My recent contributions (FR-009) ─────────────── */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>My recent contributions</div>
        <DataTable<ContributionRowShape>
          rows={contributionsSlice}
          appendOnly={true}
          rowTestId="task-row"
          onRowClick={(c) => navigate(`/task/${c.id}`)}
          columns={[
            { key: "title", header: "Task", render: (r) => r.title },
            { key: "costDisplay", header: "Cost", render: (r) => r.costDisplay },
            { key: "elapsedRelative", header: "Elapsed", render: (r) => r.elapsedRelative },
            {
              key: "reflectionLink",
              header: "Reflection",
              render: (r) => (
                <a
                  href={r.reflectionHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  role="link"
                >
                  Report
                </a>
              ),
            },
          ]}
          emptyState={
            <div style={{ opacity: 0.6, padding: "16px 0" }}>
              No completed tasks yet. Once you ship something, it&apos;ll show here.
            </div>
          }
        />
      </div>
    </PageShell>
  );
}
